/**
 * Veroni search — structured query builder (2026-09-23) + exact 4-digit SKU routing.
 *
 * The catalog's tags are normalised (_pipeline/tag_audit/fix_tags.py): every product carries gemstone-<stone>
 * for each stone it sells, gold-<colour>, karat-10k / karat-9k / karat-14k-vermeil, and its product type. Storefront
 * search supports `tag:` and `product_type:` field terms (AND-ed), so shopper words are mapped onto them:
 *   stones (plus plurals, common misspellings, birthstone months)  -> tag:gemstone-<stone>
 *   necklace/earring/ring/bracelet words (+ misspellings)          -> product_type:<Type>   (+ the subtype word
 *                                                                      as text for stud/hoop/huggie/drop/tennis…)
 *   vermeil / 14k                                                  -> tag:karat-14k-vermeil
 *   solid / 10k / 9k                                               -> tag:karat-10k (tag:karat-9k on /en-eu)
 *   yellow|white|rose gold                                         -> tag:gold-<colour>
 * Everything else stays free text (design names, heart, cross, initial…). The shopper's own words travel as `oq`
 * for display; the search page retries with the plain words if the structured query finds nothing.
 * The search section reads metal intent from the query so solid-gold searches are not swapped to vermeil.
 */
(function () {
  var SKU_RE = /^\d{4}$/;
  var STONES = {
    'blue topaz': 'blue-topaz', 'white topaz': 'white-topaz', 'pink topaz': 'pink-topaz', 'green amethyst': 'green-amethyst',
    'lemon quartz': 'lemon-quartz', 'rose quartz': 'rose-quartz', 'smoky quartz': 'smoky-quartz', 'green sapphire': 'green-sapphire',
    'pink sapphire': 'pink-sapphire', 'black onyx': 'black-onyx', 'black spinel': 'black-spinel', 'black pearl': 'black-pearl',
    'cubic zirconia': 'zirconia', 'multi stone': 'multi-stone', 'multi-stone': 'multi-stone', 'multistone': 'multi-stone',
    'lab diamond': 'diamond', 'lab-grown diamond': 'diamond',
    amethyst: 'amethyst', amethysts: 'amethyst', amythest: 'amethyst', amethist: 'amethyst', amathyst: 'amethyst', amythyst: 'amethyst',
    aquamarine: 'aquamarine', aquamarines: 'aquamarine', aquamarin: 'aquamarine', aquamarina: 'aquamarine',
    citrine: 'citrine', citrines: 'citrine', citrin: 'citrine',
    diamond: 'diamond', diamonds: 'diamond', diamon: 'diamond', dimond: 'diamond',
    emerald: 'emerald', emeralds: 'emerald', emarald: 'emerald', emrald: 'emerald', emerold: 'emerald',
    garnet: 'garnet', garnets: 'garnet', garnett: 'garnet',
    opal: 'opal', opals: 'opal',
    pearl: 'pearl', pearls: 'pearl', perl: 'pearl',
    peridot: 'peridot', peridots: 'peridot', peridote: 'peridot', perdot: 'peridot',
    ruby: 'ruby', rubies: 'ruby', rubys: 'ruby', rubi: 'ruby',
    sapphire: 'sapphire', sapphires: 'sapphire', saphire: 'sapphire', saphires: 'sapphire', sapphir: 'sapphire', saphhire: 'sapphire',
    tanzanite: 'tanzanite', tanzinite: 'tanzanite', tanzanit: 'tanzanite',
    alexandrite: 'alexandrite', alexandrit: 'alexandrite', spinel: 'spinel', zirconia: 'zirconia', cz: 'zirconia',
    onyx: 'black-onyx',
  };
  var MONTHS = { january: 'garnet', jan: 'garnet', february: 'amethyst', feb: 'amethyst', march: 'aquamarine', mar: 'aquamarine',
    april: 'diamond', apr: 'diamond', may: 'emerald', june: 'alexandrite', jun: 'alexandrite', july: 'ruby', jul: 'ruby',
    august: 'peridot', aug: 'peridot', september: 'sapphire', sept: 'sapphire', sep: 'sapphire', october: 'opal', oct: 'opal',
    november: 'citrine', nov: 'citrine', december: 'blue-topaz', dec: 'blue-topaz' };
  var TYPES = {
    necklace: 'Necklaces', necklaces: 'Necklaces', neckless: 'Necklaces', necklase: 'Necklaces', neclace: 'Necklaces', necklce: 'Necklaces',
    pendant: 'Necklaces', pendants: 'Necklaces', pendent: 'Necklaces', chain: 'Necklaces', chains: 'Necklaces',
    earring: 'Earrings', earrings: 'Earrings', earing: 'Earrings', earings: 'Earrings', earrring: 'Earrings',
    stud: 'Earrings', studs: 'Earrings', hoop: 'Earrings', hoops: 'Earrings', huggie: 'Earrings', huggies: 'Earrings',
    drop: 'Earrings', drops: 'Earrings', dangle: 'Earrings', dangles: 'Earrings', dangling: 'Earrings', chandelier: 'Earrings',
    chandeliers: 'Earrings', leverback: 'Earrings', leverbacks: 'Earrings',
    ring: 'Rings', rings: 'Rings', band: 'Rings', bands: 'Rings',
    bracelet: 'Bracelets', bracelets: 'Bracelets', braclet: 'Bracelets', bracelett: 'Bracelets', tennis: 'Bracelets', bangle: 'Bracelets',
  };
  // subtype words that should still narrow the text match inside their type
  var SUBTYPE = { stud: 'stud', studs: 'stud', hoop: 'hoop', hoops: 'hoop', huggie: 'huggie', huggies: 'huggie', drop: 'drop',
    drops: 'drop', dangle: 'dangl', dangles: 'dangl', dangling: 'dangl', chandelier: 'chandelier', chandeliers: 'chandelier',
    leverback: 'leverback', leverbacks: 'leverback', tennis: 'tennis', pendant: 'pendant', pendants: 'pendant', pendent: 'pendant' };
  var STOP = { gold: 1, golden: 1, jewelry: 1, jewellery: 1, jewel: 1, the: 1, a: 1, an: 1, for: 1, and: 1, with: 1, in: 1, of: 1,
    lab: 1, grown: 1, 'lab-grown': 1, labgrown: 1, birthstone: 1, birthstones: 1, gemstone: 1, gemstones: 1, gem: 1, stone: 1,
    stones: 1, piece: 1, pieces: 1, veroni: 1, womens: 1, "women's": 1, women: 1 };

  function eu() { return /^\/en-eu(\/|$)/.test(window.location.pathname); }

  function buildQuery(raw) {
    var q = String(raw || '').trim();
    if (!q || SKU_RE.test(q)) return q;
    var text = ' ' + q.toLowerCase().replace(/[’']/g, "'").replace(/[^a-z0-9' -]+/g, ' ').replace(/\s+/g, ' ') + ' ';
    var stones = [], type = null, karat = null, golds = [], words = [];
    // multi-word stones first
    Object.keys(STONES).filter(function (k) { return k.indexOf(' ') !== -1; }).forEach(function (k) {
      if (text.indexOf(' ' + k + ' ') !== -1) { stones.push(STONES[k]); text = text.replace(' ' + k + ' ', ' '); }
    });
    ['yellow', 'white', 'rose'].forEach(function (c) {
      var re = new RegExp(' ' + c + ' gold ');
      if (re.test(text)) { golds.push('gold-' + c); text = text.replace(re, ' '); }
    });
    text.trim().split(' ').forEach(function (t) {
      if (!t) return;
      if (STONES[t]) return stones.push(STONES[t]);
      if (MONTHS[t]) return stones.push(MONTHS[t]);
      if (TYPES[t]) { type = TYPES[t]; if (SUBTYPE[t]) words.push(SUBTYPE[t]); return; }
      if (t === 'vermeil' || t === '14k' || t === 'plated') return (karat = 'karat-14k-vermeil');
      if (t === 'solid' || t === '10k' || t === '9k' || t === '10kt' || t === '9kt') return (karat = eu() ? 'karat-9k' : 'karat-10k');
      if ((t === 'yellow' || t === 'white') && golds.indexOf('gold-' + t) === -1) return golds.push('gold-' + t);
      if (STOP[t]) return;
      words.push(t);
    });
    var parts = [];
    stones.filter(function (s, i) { return stones.indexOf(s) === i; }).forEach(function (s) { parts.push('tag:gemstone-' + s); });
    if (type) parts.push('product_type:' + type);
    if (karat) parts.push('tag:' + karat);
    golds.forEach(function (g) { parts.push('tag:' + g); });
    if (!parts.length) return q;                       // nothing structured: keep the shopper's words untouched
    return parts.concat(words).join(' ');
  }
  window.veroniBuildSearchQuery = buildQuery;

  function enhanceForm(form) {
    if (!form || form.dataset.veroniSearchReady) return;
    form.dataset.veroniSearchReady = '1';
    form.addEventListener('submit', function () {
      var input = form.querySelector('input[name="q"], input[type="search"]');
      if (!input) return;
      var original = input.value.trim();
      if (!original || SKU_RE.test(original)) return;
      var built = buildQuery(original);
      if (built !== original) {
        var hidden = form.querySelector('input[name="oq"]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'oq';
          form.appendChild(hidden);
        }
        hidden.value = original;
        input.value = built;
        // restore what the shopper typed if they come back to this page (bfcache)
        window.addEventListener('pageshow', function () { input.value = original; }, { once: true });
      }
    });
  }

  // Search page: show the shopper's own words (oq) and, if the structured query found nothing, retry once with
  // their plain words (Liquid cannot read query params, so this lives here).
  function searchPage() {
    // exact /search (optionally /xx or /xx-yy locale prefix): '//evil.example/search' must never match
    if (!/^(\/[a-z]{2}(-[a-z]{2})?)?\/search\/?$/i.test(location.pathname)) return;
    var params = new URLSearchParams(location.search);
    var oq = params.get('oq');
    if (!oq) return;
    var input = document.querySelector('.v-search-page__field input[name="q"]');
    if (input) input.value = oq;
    var strong = document.querySelector('.v-search-page__status strong');
    if (strong) strong.textContent = oq;
    var hasResults = !!document.querySelector('[data-veroni-search-filters]');   // toolbar renders only with results
    if (!hasResults && !params.get('fb') && params.get('q') !== oq && !SKU_RE.test(oq)) {
      params.set('q', oq);
      params.set('fb', '1');
      location.replace(location.origin + location.pathname + '?' + params.toString());   // same-origin only
    }
  }

  function boot() {
    document.querySelectorAll('form[action*="/search"], form[role="search"]').forEach(enhanceForm);
    searchPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
